/* ═══════════════════════════════════════════════════════════════════════
   BAKE LIST — internal production-planning tool
   Parses Shopify orders CSV, aggregates by flavor, renders production view.
   Runs 100% client-side. No data leaves the browser.
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  /* ── State ──────────────────────────────────────────────────────── */
  let orders = {};      // { '#1036': { name, customer, email, items: [...] } }
  let allDates = [];    // sorted list of unique pickup/delivery dates seen

  /* ── DOM refs ───────────────────────────────────────────────────── */
  const root        = $('[data-bake-list]');
  const uploadZone  = $('[data-upload-zone]');
  const fileInput   = $('#bake-list-file');
  const dateFilter  = $('[data-date-filter]');
  const dateSelect  = $('[data-date-select]');
  const resetBtn    = $('[data-reset]');
  const errorBox    = $('[data-error]');
  const reportBox   = $('[data-report]');

  if (!root) return;

  /* ── Init ───────────────────────────────────────────────────────── */
  function init () {
    fileInput.addEventListener('change', (e) => {
      const f = e.target.files && e.target.files[0];
      if (f) handleFile(f);
    });

    uploadZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      uploadZone.classList.add('is-dragover');
    });
    uploadZone.addEventListener('dragleave', () => {
      uploadZone.classList.remove('is-dragover');
    });
    uploadZone.addEventListener('drop', (e) => {
      e.preventDefault();
      uploadZone.classList.remove('is-dragover');
      const f = e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) handleFile(f);
    });

    dateSelect.addEventListener('change', () => {
      render(dateSelect.value);
    });

    resetBtn.addEventListener('click', () => {
      orders = {};
      allDates = [];
      fileInput.value = '';
      uploadZone.hidden = false;
      dateFilter.hidden = true;
      errorBox.hidden = true;
      reportBox.hidden = true;
      reportBox.innerHTML = '';
    });
  }

  /* ── File handling ──────────────────────────────────────────────── */
  async function handleFile (file) {
    hideError();
    if (!file.name.toLowerCase().endsWith('.csv')) {
      return showError('That doesn\'t look like a .csv file. Please drop a Shopify orders CSV export.');
    }

    // Wait for PapaParse if it hasn't finished loading yet (defer)
    let waited = 0;
    while (typeof window.Papa === 'undefined' && waited < 3000) {
      await new Promise(r => setTimeout(r, 100));
      waited += 100;
    }
    if (typeof window.Papa === 'undefined') {
      return showError('CSV parsing library failed to load. Check your internet connection and refresh the page.');
    }

    try {
      const text = await file.text();
      const result = window.Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
      });

      if (result.errors && result.errors.length) {
        console.warn('CSV parse warnings:', result.errors);
      }
      if (!result.data || !result.data.length) {
        return showError('No rows found in the CSV. Try re-exporting from Shopify.');
      }

      const built = buildOrdersFromRows(result.data);
      if (Object.keys(built.orders).length === 0) {
        return showError(
          'No orders found in the CSV. Make sure you used the **"Orders, transactions, and refunds"** export format ' +
          '(Shopify Admin → Orders → Export → choose that option). The plain "Orders" export is missing the line item details we need.'
        );
      }

      orders = built.orders;
      allDates = built.dates;
      populateDateFilter(allDates);
      uploadZone.hidden = true;
      dateFilter.hidden = false;
      // Default filter: tomorrow if we have it, else first available, else "all"
      const defaultDate = pickDefaultDate(allDates);
      dateSelect.value = defaultDate;
      render(defaultDate);
    } catch (err) {
      console.error(err);
      showError('Couldn\'t parse that CSV. Error: ' + err.message);
    }
  }

  /* ── Build orders structure from raw CSV rows ───────────────────── */
  function buildOrdersFromRows (rows) {
    const result = {};
    const dateSet = new Set();

    // Detect column names — Shopify has changed naming over time
    const sample = rows[0];
    const cols = getColMap(sample);

    // Order-level metadata cache: Note Attributes only populate on each
    // order's FIRST row in the CSV, so we capture them once and reuse for
    // every line item that belongs to that order. This is also where the
    // Flavors data lives in the standard Shopify CSV export — line item
    // properties don't export, but cart attributes (Note Attributes) do.
    const orderMeta = {};
    // Per-order queue of unconsumed flavor entries (one per multi-flavor box).
    // We pop from this as we encounter matching box line items.
    const flavorQueue = {};

    rows.forEach((row) => {
      const orderName = String(row[cols.name] || '').trim();
      if (!orderName) return;
      const liName = String(row[cols.liName] || '').trim();
      if (!liName) return;
      // Filter out Tip line items — they're Shopify-injected for gratuity,
      // not products to bake.
      if (/^tip$/i.test(liName)) return;

      // Capture order-level meta on first encounter.
      if (!orderMeta[orderName]) {
        const noteAttrsRaw = String(row[cols.noteAttrs] || '');
        const meta = parseNoteAttributes(noteAttrsRaw);
        // Parse the order's Notes column for the "Allergy items:" block that
        // `order-scheduler-cart.js` writes via `syncAllergyToNote()`. Each line
        // is `- <Product Title>: <Allergy>` — we map them by product title so
        // we can attach allergy info to the matching line item below.
        const notesRaw = String(row[cols.notes] || '');
        const allergyByTitle = {};
        const allergyBlockMatch = notesRaw.match(/Allergy items:\n((?:- [^\n]+\n?)+)/);
        if (allergyBlockMatch) {
          allergyBlockMatch[1].split(/\r?\n/).forEach((line) => {
            const m = line.match(/^-\s+(.+?):\s+(.+?)\s*$/);
            if (m) allergyByTitle[m[1].trim()] = m[2].trim();
          });
        }
        meta._allergyByTitle = allergyByTitle;
        orderMeta[orderName] = meta;
        // Pre-build the flavor queue: array of {size, flavors:[{title,qty}]}
        // for every `Flavors...` key in note_attributes. We'll match each
        // entry to a matching-size box line item below.
        const fq = [];
        Object.keys(meta).forEach((k) => {
          if (/^Flavors\b/i.test(k)) {
            const parsed = parseFlavorsString(meta[k]);
            if (parsed) {
              // Extract size hint from key like "Flavors (12 Cupcakes)" or
              // "Flavors - Box 1 (4 Cupcakes)". Falls back to NaN; matcher
              // treats NaN as "match any multi-flavor box".
              const sizeMatch = k.match(/\((\d+)\s*Cupcakes?\)/i);
              const size = sizeMatch ? parseInt(sizeMatch[1], 10) : NaN;
              fq.push({ key: k, size, flavors: parsed });
            }
          }
        });
        flavorQueue[orderName] = fq;
      }
      const meta = orderMeta[orderName];

      const pickupDate  = meta['Pickup/Delivery Date'] || meta['Pickup Date'] || meta['Delivery Date'] || '';
      const pickupTime  = meta['Pickup/Delivery Time'] || meta['Pickup Time'] || meta['Delivery Time'] || '';
      const orderType   = meta['Order Type'] || (meta['Delivery Zip'] ? 'Delivery' : 'Pickup');
      const deliveryInstructions = meta['Delivery Instructions'] || '';
      const deliveryZip = meta['Delivery Zip'] || '';
      // Allergy info lookup (in priority order):
      // 1. The Notes-column "Allergy items:" block parsed at order-meta capture
      //    (current approach — `syncAllergyToNote()` writes here).
      // 2. The legacy `Allergy - <Product Title>` Note Attribute key (from the
      //    earlier cart-attribute approach we briefly shipped).
      // 3. The order-level `Allergy Friendly` key for single-allergy orders.
      // The line-item-property fallback (`liProps['Allergy Friendly']`) is
      // applied separately below since liProps come from a different column.
      function lookupAllergy (rawTitle, cleanedTitle) {
        const allergyMap = meta._allergyByTitle || {};
        if (allergyMap[rawTitle]) return allergyMap[rawTitle];
        if (allergyMap[cleanedTitle]) return allergyMap[cleanedTitle];
        const altKeys = [
          'Allergy - ' + rawTitle,
          'Allergy - ' + cleanedTitle,
        ];
        for (const key of altKeys) {
          if (meta[key]) return meta[key];
        }
        return meta['Allergy Friendly'] || '';
      }

      // Line item properties column (often empty in standard Shopify exports
      // — kept for legacy/property-rich exports).
      const liPropertiesRaw = String(row[cols.liProperties] || '');
      const liProps = parseLineItemProperties(liPropertiesRaw);
      const liFlavorsText = liProps['Flavors'] || '';

      // Title cleanup: Shopify CSV appends variant as " - VariantName".
      // Split into base title + variant so the display can show them
      // separately and so categorization regex doesn't trip over variant
      // text (e.g. "Vanilla Vanilla Cake - 6-Inch" → title "Vanilla Vanilla
      // Cake", variant "6-Inch").
      let cleanTitle = liName;
      let cleanVariant = String(row[cols.liVariant] || '').trim();
      const variantSplit = liName.split(/\s+-\s+/);
      if (variantSplit.length === 2 && !cleanVariant) {
        cleanTitle = variantSplit[0].trim();
        cleanVariant = variantSplit[1].trim();
      }

      const liQty = parseInt(row[cols.liQty], 10) || 1;
      const liSku = String(row[cols.liSku] || '').trim();

      // Determine boxContents — match to flavor queue by size if this is a
      // multi-flavor box product. Otherwise fall back to line item property.
      let boxContents = null;
      const isMultiBox = /^(\d+)\s+Cupcakes?$/i.test(cleanTitle);
      if (isMultiBox && flavorQueue[orderName] && flavorQueue[orderName].length) {
        const thisSize = parseInt(cleanTitle.match(/^(\d+)/)[1], 10) * liQty;
        // Try to match by size first; else take the first available entry.
        const fq = flavorQueue[orderName];
        let idx = fq.findIndex(e => e.size === thisSize);
        if (idx < 0) idx = 0;
        boxContents = fq[idx].flavors;
        fq.splice(idx, 1);
      }
      if (!boxContents) {
        boxContents = parseFlavorsString(liFlavorsText)
          || parseBoxContents(liProps['_box_contents'] || '');
      }

      const cupcakePick = liProps['Cupcake Pick'] || meta['Cupcake Pick'] || '';

      if (pickupDate) dateSet.add(pickupDate);

      // Initialize order if first line item seen for it
      if (!result[orderName]) {
        result[orderName] = {
          name: orderName,
          customer: String(row[cols.billingName] || row[cols.shippingName] || '').trim() || 'Unknown',
          email: String(row[cols.email] || '').trim(),
          total: String(row[cols.total] || '').trim(),
          created: String(row[cols.created] || '').trim(),
          items: [],
        };
      }

      // Resolve allergy info now that we have both the raw and cleaned title
      // (used to match against `Allergy - <Product>` keys in Note Attributes,
      // or fall back to legacy line item property / order-level key).
      const allergyFlag = liProps['Allergy Friendly']
        || lookupAllergy(liName, cleanTitle)
        || '';

      result[orderName].items.push({
        title: cleanTitle,
        variant: cleanVariant,
        qty: liQty,
        sku: liSku,
        pickupDate,
        pickupTime,
        orderType,
        allergyFlag,
        cupcakePick,
        deliveryInstructions,
        deliveryZip,
        flavorsText: liFlavorsText,
        boxContents,
      });
    });

    const dates = Array.from(dateSet).sort((a, b) => {
      const da = new Date(a); const db = new Date(b);
      if (isNaN(da) || isNaN(db)) return a.localeCompare(b);
      return da - db;
    });

    return { orders: result, dates };
  }

  /* ── Map a sample row's keys to column-name variations ──────────── */
  function getColMap (sample) {
    const keys = Object.keys(sample);
    const findKey = (...needles) => {
      for (const needle of needles) {
        const k = keys.find(k => k.toLowerCase().trim() === needle.toLowerCase());
        if (k) return k;
      }
      // fallback: partial match
      for (const needle of needles) {
        const k = keys.find(k => k.toLowerCase().includes(needle.toLowerCase()));
        if (k) return k;
      }
      return null;
    };

    return {
      name:          findKey('Name'),
      email:         findKey('Email'),
      total:         findKey('Total'),
      created:       findKey('Created at', 'Paid at'),
      liName:        findKey('Lineitem name', 'Line item name', 'Lineitem'),
      liQty:         findKey('Lineitem quantity', 'Line item quantity', 'Quantity'),
      liSku:         findKey('Lineitem sku', 'Line item sku', 'SKU'),
      liVariant:     findKey('Lineitem variant', 'Line item variant', 'Variant'),
      liProperties:  findKey('Lineitem properties', 'Line item properties', 'Properties'),
      notes:         findKey('Notes'),
      noteAttrs:     findKey('Note Attributes', 'Note Attribute'),
      billingName:   findKey('Billing Name', 'Billing name'),
      shippingName:  findKey('Shipping Name', 'Shipping name'),
    };
  }

  /* ── Parse the line item properties cell ────────────────────────── */
  /*  Shopify format varies. Common formats:
   *    Key: Value\nKey2: Value2
   *    Key: Value | Key2: Value2
   *    {"Key": "Value", ...}  (JSON)
   *  We try them in order. */
  function parseLineItemProperties (raw) {
    if (!raw) return {};
    const out = {};

    // Try JSON first (rare but possible)
    if (raw.trim().startsWith('{')) {
      try { return JSON.parse(raw); } catch (e) { /* fall through */ }
    }

    // _box_contents can have JSON with commas/brackets — extract that piece first
    // by finding `_box_contents:[...]` or `_box_contents: [...]`
    let working = raw;
    const boxMatch = working.match(/_box_contents\s*:\s*(\[.*?\])(?=\s*[\n|]|\s*[A-Za-z_]+\s*:|$)/s);
    if (boxMatch) {
      out['_box_contents'] = boxMatch[1];
      working = working.replace(boxMatch[0], '').trim();
    }

    // Now parse remaining key:value pairs
    // Try newline-separated first
    let pairs = working.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    if (pairs.length <= 1) {
      // Maybe pipe-separated
      pairs = working.split('|').map(s => s.trim()).filter(Boolean);
    }
    if (pairs.length <= 1) {
      // Last resort: split on ", " but only if the pair looks like Key: Value
      // (this is heuristic — values with embedded commas like dates may merge)
      pairs = splitOnPropertyBoundary(working);
    }

    pairs.forEach((pair) => {
      const idx = pair.indexOf(':');
      if (idx < 1) return;
      const key = pair.slice(0, idx).trim();
      const val = pair.slice(idx + 1).trim();
      if (key) out[key] = val;
    });

    return out;
  }

  /* ── Heuristic property-boundary splitter ───────────────────────── */
  function splitOnPropertyBoundary (str) {
    // Look for pattern: ", Word(s):" → that's a property boundary
    // Known property keys to anchor on
    const knownKeys = [
      'Order Type', 'Pickup/Delivery Date', 'Pickup/Delivery Time',
      'Delivery Instructions', 'Delivery Zip', 'Cupcake Pick',
      'Allergy Friendly', 'Gluten Friendly', 'Flavors', '_box_contents',
      'Pickup Date', 'Pickup Time', 'Delivery Date', 'Delivery Time',
    ];
    const escapedKeys = knownKeys.map(k => k.replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&'));
    const regex = new RegExp(`,\\s*(?=(?:${escapedKeys.join('|')})\\s*:)`, 'g');
    return str.split(regex).map(s => s.trim()).filter(Boolean);
  }

  /* ── Parse the `Note Attributes` column from Shopify CSV export ──── */
  /*  Multi-line cell of `Key: Value` pairs, newline-separated. This is
   *  where Shopify exports order-level cart attributes: Order Type,
   *  Pickup/Delivery Date, Pickup/Delivery Time, Delivery Instructions,
   *  Delivery Zip, and (post-2026-05-11) the `Flavors (...)` entries that
   *  the order wizard mirrors there for CSV visibility. */
  function parseNoteAttributes (raw) {
    if (!raw || !raw.trim()) return {};
    const out = {};
    raw.split(/\r?\n/).forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const idx = trimmed.indexOf(':');
      if (idx < 1) return;
      const key = trimmed.slice(0, idx).trim();
      const val = trimmed.slice(idx + 1).trim();
      if (key) out[key] = val;
    });
    return out;
  }

  /* ── Parse `Flavors` human-readable string ──────────────────────── */
  /*  Format: "1x Salted Caramel, 2x Carrot Gluten Friendly, 1x ..."
   *  This is the primary source post-2026-05-11 cleanup. Flavor names
   *  never contain commas in current product catalog, so comma-split is
   *  safe.  Returns null if string doesn't look like a flavor list (so
   *  callers can fall back to _box_contents JSON for legacy orders). */
  function parseFlavorsString (raw) {
    if (!raw || !raw.trim()) return null;
    // Must look like "Nx Name, Nx Name" — bail otherwise
    if (!/\d+x\s/i.test(raw)) return null;
    const items = [];
    const regex = /(\d+)\s*x\s+([^,]+?)(?=\s*,\s*\d+\s*x\s|$)/gi;
    let m;
    while ((m = regex.exec(raw)) !== null) {
      const qty = parseInt(m[1], 10);
      const title = m[2].trim();
      if (qty > 0 && title) items.push({ title, qty });
    }
    return items.length ? items : null;
  }

  /* ── Parse legacy _box_contents JSON string ─────────────────────── */
  /*  Pre-2026-05-11 orders had a redundant `_box_contents` JSON property
   *  alongside `Flavors`. Kept for backward compatibility — any order
   *  placed before the cleanup will still parse correctly. */
  function parseBoxContents (raw) {
    if (!raw || !raw.trim()) return null;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) { /* try repair */ }
    // Try simple regex extraction as fallback
    try {
      const items = [];
      const regex = /"title"\s*:\s*"([^"]+)"\s*,\s*"qty"\s*:\s*(\d+)/g;
      let m;
      while ((m = regex.exec(raw)) !== null) {
        items.push({ title: m[1], qty: parseInt(m[2], 10) });
      }
      return items.length ? items : null;
    } catch (e) {
      return null;
    }
  }

  /* ── Populate date filter dropdown ──────────────────────────────── */
  function populateDateFilter (dates) {
    while (dateSelect.options.length > 1) dateSelect.remove(1);
    dates.forEach((d) => {
      const opt = document.createElement('option');
      opt.value = d;
      opt.textContent = d;
      dateSelect.appendChild(opt);
    });
  }

  function pickDefaultDate (dates) {
    if (!dates.length) return 'all';
    // Default to the EARLIEST upcoming date (most likely "tomorrow" if export
    // included unfulfilled orders only)
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const upcoming = dates.find((d) => {
      const dd = new Date(d);
      return !isNaN(dd) && dd >= today;
    });
    return upcoming || dates[0];
  }

  /* ── Render ─────────────────────────────────────────────────────── */
  function render (dateFilterValue) {
    reportBox.hidden = false;

    // Collect filtered items across all orders.
    // Special handling for orders whose line items have NO Pickup/Delivery Date
    // attribute set — these would otherwise vanish from the filtered view, which
    // is dangerous (the baker might miss them entirely). When the customer is
    // viewing a specific date, we treat dateless orders as candidates for THAT
    // date and surface them in a dedicated callout section.
    const filteredOrders = {};
    const datelessOrders = {};
    Object.values(orders).forEach((ord) => {
      // Items that explicitly match the filter
      const matchItems = ord.items.filter((it) => dateFilterValue === 'all' || it.pickupDate === dateFilterValue);
      // Items with NO date at all — possible orphans
      const datelessItems = ord.items.filter((it) => !it.pickupDate);
      if (matchItems.length) {
        filteredOrders[ord.name] = { ...ord, items: matchItems };
      } else if (datelessItems.length && dateFilterValue !== 'all') {
        // Order has no items for this date AND has line items with no date —
        // surface it as a needs-attention orphan so it doesn't silently disappear.
        datelessOrders[ord.name] = { ...ord, items: datelessItems };
      }
    });

    if (!Object.keys(filteredOrders).length && !Object.keys(datelessOrders).length) {
      reportBox.innerHTML = `
        <div class="bl-section">
          <div class="bl-section__body" style="text-align:center; padding:40px 20px; color:#6b5a55;">
            <div style="font-size:48px;">✨</div>
            <div style="font-size:18px; font-weight:700; margin-top:10px;">No orders for ${escapeHtml(dateFilterValue)}.</div>
            <div style="margin-top:6px;">Rest up — looks like a clear day.</div>
          </div>
        </div>`;
      return;
    }

    // Build aggregates
    const totals = buildTotals(filteredOrders);

    // Track total orders across all dates (not just filtered) so the stats
    // bar can tell the user "showing 2 of 5" — the original behavior just
    // showed the filtered count, which Mike flagged 2026-05-13 as confusing
    // (CSV had 5 orders, page said 2, no indication the other 3 were on
    // different pickup dates).
    const totalOrderCount = Object.keys(orders).length;

    // Render sections
    let html = '';
    html += renderActions(dateFilterValue);
    html += renderFilterBanner(filteredOrders, datelessOrders, totalOrderCount, dateFilterValue);
    html += renderStats(totals, filteredOrders, datelessOrders);
    html += renderAllergyAlerts(totals);
    html += renderDatelessOrdersAlert(datelessOrders);
    html += renderMissingFlavorBoxes(totals);
    html += renderProductionTotals(totals);
    html += renderFulfillment(filteredOrders);
    html += renderOrderCards(filteredOrders, datelessOrders);
    html += renderFutureHeadsUp(dateFilterValue);

    reportBox.innerHTML = html;

    // Wire print buttons. Each button toggles a body-level mode class that
    // controls which sections appear on the printed page (see bake-list.css
    // @media print rules), then triggers window.print(). The mode class is
    // removed after the print dialog closes so the on-screen view returns
    // to normal.
    function printWithMode (modeClass) {
      document.body.classList.add(modeClass);
      // afterprint fires after the dialog closes (regardless of success/cancel)
      const cleanup = () => {
        document.body.classList.remove(modeClass);
        window.removeEventListener('afterprint', cleanup);
      };
      window.addEventListener('afterprint', cleanup);
      // Some browsers (Safari) don't reliably fire afterprint — fall back
      // to a timer that strips the class if it lingers.
      setTimeout(cleanup, 5000);
      window.print();
    }

    const printAllBtn = $('[data-print="all"]', reportBox);
    if (printAllBtn) printAllBtn.addEventListener('click', () => printWithMode('bl-print-mode--all'));

    const printSummaryBtn = $('[data-print="summary"]', reportBox);
    if (printSummaryBtn) printSummaryBtn.addEventListener('click', () => printWithMode('bl-print-mode--summary'));

    const printCardsBtn = $('[data-print="cards"]', reportBox);
    if (printCardsBtn) printCardsBtn.addEventListener('click', () => printWithMode('bl-print-mode--cards'));

    // \"Show all dates\" pivot from the filter banner — quietly switches the
    // dropdown to 'all' and re-renders so the user can see every order in
    // the CSV without hunting for the dropdown.
    const showAllBtn = $('[data-show-all]', reportBox);
    if (showAllBtn) showAllBtn.addEventListener('click', () => {
      dateSelect.value = 'all';
      render('all');
    });

    // Pulse the date filter bar when there are orders on other dates the
    // user might miss. Same visual logic as the banner so the eye is
    // guided to the dropdown if they want to switch.
    const visibleOrderCount = Object.keys(filteredOrders).length + Object.keys(datelessOrders || {}).length;
    if (dateFilterValue !== 'all' && visibleOrderCount < totalOrderCount) {
      dateFilter.classList.add('bake-list__date-filter--has-hidden');
    } else {
      dateFilter.classList.remove('bake-list__date-filter--has-hidden');
    }
  }

  /* ── Render: dateless-orders alert ──────────────────────────────── */
  /*  Orders whose line items have NO Pickup/Delivery Date attribute. This
   *  usually means the customer reached checkout without picking a date —
   *  a defect somewhere upstream. The baker needs to know about these so
   *  they don't get silently dropped. */
  function renderDatelessOrdersAlert (datelessOrders) {
    const names = Object.keys(datelessOrders);
    if (!names.length) return '';
    let html = `
      <div class="bl-section" style="border: 2px solid #d12c2c;">
        <div class="bl-section__head" style="background: #ffe5e5;">
          <h2 style="color: #7a1818; margin: 0; font-size: 17px;">🚨 Orders with NO pickup/delivery date set</h2>
        </div>
        <div class="bl-section__body">
          <p style="margin: 0 0 12px; font-size: 14px; color: #6b5a55;">
            These orders are in the CSV but have <strong>no Pickup/Delivery Date</strong> attribute. They won't show in the date-filtered view below. Open each in Shopify admin to confirm what the customer wanted, or call them.
          </p>
    `;
    names.sort().forEach((name) => {
      const ord = datelessOrders[name];
      const isDelivery = ord.items.some(it => /delivery/i.test(it.orderType));
      html += `
        <div class="bl-order" style="border-color: #d12c2c;">
          <div class="bl-order__head">
            <span class="bl-order__name">${escapeHtml(name)}</span>
            <span class="bl-order__type ${isDelivery ? 'bl-order__type--delivery' : 'bl-order__type--pickup'}">${isDelivery ? 'DELIVERY' : 'PICKUP'} · no date</span>
          </div>
          <div class="bl-order__meta">
            ${escapeHtml(ord.customer)}${ord.email ? ` · ${escapeHtml(ord.email)}` : ''}${ord.total ? ` · ${escapeHtml(ord.total)}` : ''}
          </div>
          <ul class="bl-order__items">
            ${ord.items.map(it => `<li>${escapeHtml(it.title)}${it.variant ? ` <em>(${escapeHtml(it.variant)})</em>` : ''}${it.qty > 1 ? ` × <strong>${it.qty}</strong>` : ''}</li>`).join('')}
          </ul>
        </div>
      `;
    });
    html += `</div></div>`;
    return html;
  }

  /* ── Render: missing-flavor boxes alert ─────────────────────────── */
  function renderMissingFlavorBoxes (totals) {
    if (!totals.missingFlavorBoxes || !totals.missingFlavorBoxes.length) return '';
    const totalCount = totals.missingFlavorBoxes.reduce((s, b) => s + b.cupcakeCount, 0);
    return `
      <div class="bl-section" style="border: 2px solid #ff8c00;">
        <div class="bl-section__head" style="background: #fff4e5;">
          <h2 style="color: #8c4a00; margin: 0; font-size: 17px;">⚠️ Cupcake boxes — flavor breakdown not in CSV (${totalCount} cupcakes)</h2>
        </div>
        <div class="bl-section__body">
          <p style="margin: 0 0 10px; color: #6b5a55; font-size: 14px;">
            These boxes were ordered before the 2026-05-11 bake-list update, so the per-flavor breakdown isn't in the CSV export. Open each order in Shopify admin to see the flavor list.
          </p>
          ${totals.missingFlavorBoxes.map(b => `
            <div class="bl-line">
              <span class="bl-line__name"><strong>${escapeHtml(b.order)}</strong> · ${escapeHtml(b.title)}${b.qty > 1 ? ` × ${b.qty}` : ''}</span>
              <span class="bl-line__qty">${b.cupcakeCount} cupcakes</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  /* ── Aggregations ───────────────────────────────────────────────── */
  function buildTotals (filteredOrders) {
    const standardCupcakeFlavors = {};   // from _box_contents
    const allergyCupcakes = [];          // 6-count boxes
    const cakes = [];
    const pies = [];
    const cookies = [];
    const brownies = [];
    const specialty = [];
    const preMade = [];                  // collections, picks, decorations
    const allergyAlerts = [];
    const missingFlavorBoxes = [];       // multi-flavor boxes without flavor data — needs lookup in Shopify

    Object.values(filteredOrders).forEach((ord) => {
      ord.items.forEach((it) => {
        const title = it.title;
        const lower = title.toLowerCase();
        const isAllergyBox = /allergy[\s-]?friendly/i.test(title) || /allergy/i.test(it.allergyFlag);
        const isPick = /cupcake picks?|pick$/i.test(title) || it.title.includes('Pick');
        const isCake = /\bcake\b/i.test(title) && !/cheesecake/i.test(title) && !/cupcake/i.test(title);
        const isCheesecake = /cheesecake/i.test(title);
        const isPie = /\bpie\b/i.test(title);
        const isCookie = /cookie/i.test(title) && !/cookies\s*&\s*cream/i.test(title);
        const isBrownie = /brownie/i.test(title);
        const isShortcake = /shortcake/i.test(title);
        const isPupcake = /pupcake|puppy cake/i.test(title);
        const isCollection = /collection|holiday|mother'?s day|father'?s day|valentine|easter/i.test(title);
        const multiPackMatch = title.match(/^(\d+)\s*cupcakes?$/i);
        const isMultiPack = !!multiPackMatch;
        const multiPackSize = isMultiPack ? parseInt(multiPackMatch[1], 10) : 0;

        // Collect allergy alerts (separate from per-product categorization)
        if (it.allergyFlag) {
          allergyAlerts.push({
            order: ord.name,
            title,
            flag: it.allergyFlag,
            qty: it.qty,
          });
        } else if (isAllergyBox) {
          allergyAlerts.push({
            order: ord.name,
            title,
            flag: 'Allergy-Friendly',
            qty: it.qty,
          });
        } else if (/gluten[\s-]?friendly/i.test(title) || /\bgf\b/i.test(title)) {
          allergyAlerts.push({
            order: ord.name,
            title,
            flag: 'Gluten Friendly',
            qty: it.qty,
          });
        }

        // Categorize
        if (isPick) {
          preMade.push({ order: ord.name, title, qty: it.qty });
        } else if (isAllergyBox) {
          allergyCupcakes.push({
            order: ord.name,
            title,
            qty: it.qty,
            flag: it.allergyFlag || 'Allergy-Friendly',
          });
        } else if (isMultiPack && it.boxContents && it.boxContents.length) {
          // Expand box contents into per-flavor totals
          it.boxContents.forEach((b) => {
            const key = b.title;
            standardCupcakeFlavors[key] = (standardCupcakeFlavors[key] || 0) + (b.qty * it.qty);
          });
        } else if (isMultiPack) {
          // Multi-flavor cupcake box BUT we have no flavor data for it. This
          // happens for orders placed before the 2026-05-11 cart-attribute fix,
          // OR for any order where the Flavors data didn't reach the CSV. The
          // baker still needs to make these cupcakes — surface them prominently
          // with a clear "look this up in Shopify" callout.
          missingFlavorBoxes.push({
            order: ord.name,
            title,
            qty: it.qty,
            boxSize: multiPackSize,
            cupcakeCount: multiPackSize * it.qty,
          });
        } else if (isShortcake) {
          specialty.push({ order: ord.name, title, qty: it.qty });
        } else if (isPupcake) {
          specialty.push({ order: ord.name, title, qty: it.qty });
        } else if (isCheesecake) {
          cakes.push({ order: ord.name, title: title + (it.variant ? ` (${it.variant})` : ''), qty: it.qty, allergy: it.allergyFlag });
        } else if (isCake) {
          cakes.push({ order: ord.name, title: title + (it.variant ? ` (${it.variant})` : ''), qty: it.qty, allergy: it.allergyFlag });
        } else if (isPie) {
          pies.push({ order: ord.name, title, qty: it.qty });
        } else if (isCookie) {
          cookies.push({ order: ord.name, title, qty: it.qty, allergy: it.allergyFlag });
        } else if (isBrownie) {
          brownies.push({ order: ord.name, title, qty: it.qty });
        } else if (isCollection) {
          preMade.push({ order: ord.name, title: title + (it.variant ? ` — ${it.variant}` : ''), qty: it.qty });
        } else if (it.boxContents && it.boxContents.length) {
          // Catch-all: anything with box contents → expand flavors
          it.boxContents.forEach((b) => {
            const key = b.title;
            standardCupcakeFlavors[key] = (standardCupcakeFlavors[key] || 0) + (b.qty * it.qty);
          });
        } else {
          // Unknown — drop into specialty so it's visible
          specialty.push({ order: ord.name, title, qty: it.qty });
        }
      });
    });

    // Sort flavors by quantity desc, then alpha
    const flavorList = Object.entries(standardCupcakeFlavors)
      .map(([title, qty]) => ({ title, qty }))
      .sort((a, b) => b.qty - a.qty || a.title.localeCompare(b.title));

    const flavorTotal = flavorList.reduce((sum, f) => sum + f.qty, 0);
    const allergyCupcakeTotal = allergyCupcakes.reduce((sum, a) => sum + a.qty * 6, 0); // each box = 6 cupcakes
    const missingFlavorCupcakeTotal = missingFlavorBoxes.reduce((sum, b) => sum + b.cupcakeCount, 0);

    return {
      standardCupcakeFlavors: flavorList,
      flavorTotal,
      allergyCupcakes,
      allergyCupcakeTotal,
      missingFlavorBoxes,
      missingFlavorCupcakeTotal,
      cakes, pies, cookies, brownies, specialty, preMade,
      allergyAlerts,
    };
  }

  /* ── Renderers ──────────────────────────────────────────────────── */
  function renderActions (dateFilter) {
    // Three print modes covering the kitchen workflow Chris asked for
    // 2026-05-13: a full packet (summary + cards), summary-only (totals to
    // keep on the counter), or cards-only (one per page to tape to each
    // tray). Mode classes are toggled on <body> right before window.print()
    // so the @media print rules can choose what to show.
    return `
      <div class="bake-list__actions">
        <button class="bake-list__action-btn" data-print="all" type="button">🖨️ Print everything</button>
        <button class="bake-list__action-btn bake-list__action-btn--secondary" data-print="summary" type="button">Summary only</button>
        <button class="bake-list__action-btn bake-list__action-btn--secondary" data-print="cards" type="button">Order cards only</button>
      </div>
    `;
  }

  /* ── Render: filter banner ──────────────────────────────────────── */
  /*  Tells the user up-front whether the page is filtered to a single date
   *  or showing everything, and how many orders are visible vs total. Mike
   *  flagged 2026-05-13: \"says there are only 2 orders. I think there are
   *  5.\" Root cause: default filter is the next upcoming date, so a CSV
   *  spanning a week showed only that day's slice with no visual cue that
   *  more existed. This banner makes the relationship explicit + offers a
   *  one-click \"Show all dates\" pivot. */
  function renderFilterBanner (filteredOrders, datelessOrders, totalOrderCount, dateFilter) {
    const visibleCount = Object.keys(filteredOrders).length + Object.keys(datelessOrders || {}).length;
    if (dateFilter === 'all') {
      // Showing everything — quiet info banner, no action button needed.
      return `
        <div class="bl-filter-banner bl-filter-banner--all">
          <span class="bl-filter-banner__icon" aria-hidden="true">📅</span>
          <div class="bl-filter-banner__text">
            Showing <strong>${totalOrderCount}</strong> order${totalOrderCount === 1 ? '' : 's'} across all dates in this CSV.
          </div>
        </div>
      `;
    }
    if (visibleCount < totalOrderCount) {
      const hidden = totalOrderCount - visibleCount;
      return `
        <div class="bl-filter-banner bl-filter-banner--filtered">
          <span class="bl-filter-banner__icon" aria-hidden="true">📅</span>
          <div class="bl-filter-banner__text">
            Showing <strong>${visibleCount}</strong> of <strong>${totalOrderCount}</strong> orders.
            Filtered to <strong>${escapeHtml(dateFilter)}</strong> — ${hidden} order${hidden === 1 ? '' : 's'} on other date${hidden === 1 ? '' : 's'} ${hidden === 1 ? 'is' : 'are'} hidden.
          </div>
          <button type="button" class="bl-filter-banner__btn" data-show-all>Show all dates</button>
        </div>
      `;
    }
    // Filter matches everything (rare — e.g. single-date CSV). Show a small
    // confirmation so the user knows nothing's hidden.
    return `
      <div class="bl-filter-banner bl-filter-banner--match">
        <span class="bl-filter-banner__icon" aria-hidden="true">📅</span>
        <div class="bl-filter-banner__text">
          Showing all <strong>${visibleCount}</strong> order${visibleCount === 1 ? '' : 's'} in this CSV — every order is for <strong>${escapeHtml(dateFilter)}</strong>.
        </div>
      </div>
    `;
  }

  function renderStats (totals, filteredOrders, datelessOrders) {
    const orderCount = Object.keys(filteredOrders).length + Object.keys(datelessOrders || {}).length;
    const lineCount = Object.values(filteredOrders).reduce((sum, o) => sum + o.items.length, 0)
      + Object.values(datelessOrders || {}).reduce((sum, o) => sum + o.items.length, 0);
    const cupcakeTotal = totals.flavorTotal + totals.allergyCupcakeTotal + (totals.missingFlavorCupcakeTotal || 0);
    return `
      <div class="bl-stats">
        <div class="bl-stats__item"><div class="bl-stats__num">${orderCount}</div><div class="bl-stats__label">Orders</div></div>
        <div class="bl-stats__item"><div class="bl-stats__num">${lineCount}</div><div class="bl-stats__label">Line Items</div></div>
        <div class="bl-stats__item"><div class="bl-stats__num">${cupcakeTotal}</div><div class="bl-stats__label">Cupcakes</div></div>
        <div class="bl-stats__item"><div class="bl-stats__num">${totals.cakes.length + totals.pies.length + totals.cookies.length + totals.brownies.length + totals.specialty.length}</div><div class="bl-stats__label">Other Items</div></div>
      </div>
    `;
  }

  function renderAllergyAlerts (totals) {
    if (!totals.allergyAlerts.length) return '';
    return `
      <div class="bl-allergy">
        <h2>⚠️ Allergy alerts — read first</h2>
        <ul>
          ${totals.allergyAlerts.map(a => `
            <li><strong>${escapeHtml(a.order)}</strong> — ${escapeHtml(a.title)}${a.qty > 1 ? ` × ${a.qty}` : ''} — <strong>${escapeHtml(a.flag.toUpperCase())}</strong></li>
          `).join('')}
        </ul>
      </div>
    `;
  }

  function renderProductionTotals (totals) {
    const sections = [];

    if (totals.standardCupcakeFlavors.length) {
      sections.push(`
        <div class="bl-section">
          <div class="bl-section__head">
            <h2>Cupcakes — Standard Flavors <span class="bl-count">(${totals.flavorTotal} total)</span></h2>
          </div>
          <div class="bl-section__body">
            <div class="bl-flavors">
              ${totals.standardCupcakeFlavors.map(f => `
                <div class="bl-flavor">
                  <span class="bl-flavor__name">${escapeHtml(f.title)}</span>
                  <span class="bl-flavor__qty">${f.qty} ×</span>
                </div>
              `).join('')}
            </div>
          </div>
        </div>
      `);
    }

    if (totals.allergyCupcakes.length) {
      sections.push(`
        <div class="bl-section">
          <div class="bl-section__head">
            <h2>Cupcakes — Allergy-Friendly <span class="bl-count">(${totals.allergyCupcakeTotal} total)</span></h2>
          </div>
          <div class="bl-section__body">
            ${totals.allergyCupcakes.map(a => `
              <div class="bl-line">
                <span class="bl-line__name">${escapeHtml(a.title)} <span class="bl-tag">${escapeHtml(a.flag)}</span></span>
                <span class="bl-line__qty">${a.qty * 6} <small style="font-weight:400;color:#6b5a55;">(${a.qty} box${a.qty > 1 ? 'es' : ''})</small></span>
              </div>
            `).join('')}
          </div>
        </div>
      `);
    }

    const categoryGroups = [
      { title: 'Cakes & Cheesecakes', items: totals.cakes },
      { title: 'Pies', items: totals.pies },
      { title: 'Cookies', items: totals.cookies },
      { title: 'Brownies', items: totals.brownies },
      { title: 'Specialty Items', items: totals.specialty },
    ];

    categoryGroups.forEach(group => {
      if (group.items.length === 0) return;
      const total = group.items.reduce((sum, i) => sum + i.qty, 0);
      sections.push(`
        <div class="bl-section">
          <div class="bl-section__head">
            <h2>${escapeHtml(group.title)} <span class="bl-count">(${total} total)</span></h2>
          </div>
          <div class="bl-section__body">
            ${group.items.map(i => `
              <div class="bl-line">
                <span class="bl-line__name">${escapeHtml(i.title)}${i.allergy ? ` <span class="bl-tag">${escapeHtml(i.allergy)}</span>` : ''}</span>
                <span class="bl-line__qty">${i.qty} ×</span>
              </div>
            `).join('')}
          </div>
        </div>
      `);
    });

    if (totals.preMade.length) {
      sections.push(`
        <div class="bl-section">
          <div class="bl-section__head">
            <h2 style="color:#6b5a55;">Pre-Made / Assemble Only <span class="bl-count">(no baking)</span></h2>
          </div>
          <div class="bl-section__body">
            ${totals.preMade.map(i => `
              <div class="bl-line">
                <span class="bl-line__name">${escapeHtml(i.title)}</span>
                <span class="bl-line__qty">${i.qty} ×</span>
              </div>
            `).join('')}
          </div>
        </div>
      `);
    }

    return sections.join('');
  }

  function renderFulfillment (filteredOrders) {
    // Sort orders by pickup time
    const ordsArr = Object.values(filteredOrders);
    ordsArr.sort((a, b) => {
      const ta = earliestTime(a); const tb = earliestTime(b);
      if (!ta && !tb) return a.name.localeCompare(b.name);
      if (!ta) return 1;
      if (!tb) return -1;
      return ta.localeCompare(tb);
    });

    let html = `
      <div class="bl-section">
        <div class="bl-section__head">
          <h2>Fulfillment — by pickup/delivery time</h2>
        </div>
        <div class="bl-section__body">
    `;

    let lastTime = '';
    ordsArr.forEach((ord) => {
      const t = earliestTime(ord) || 'Time TBD';
      if (t !== lastTime) {
        html += `<div class="bl-time-block">${escapeHtml(t)}</div>`;
        lastTime = t;
      }

      const isDelivery = ord.items.some(it => /delivery/i.test(it.orderType));
      const typeLabel = isDelivery ? 'DELIVERY' : 'PICKUP';
      const typeClass = isDelivery ? 'bl-order__type--delivery' : 'bl-order__type--pickup';
      const zip = ord.items.find(it => it.deliveryZip)?.deliveryZip;
      const deliveryNote = ord.items.find(it => it.deliveryInstructions)?.deliveryInstructions;

      html += `
        <div class="bl-order">
          <div class="bl-order__head">
            <span class="bl-order__name">${escapeHtml(ord.name)}</span>
            <span class="bl-order__type ${typeClass}">${typeLabel}${zip ? ` · zip ${escapeHtml(zip)}` : ''}</span>
          </div>
          <div class="bl-order__meta">
            ${escapeHtml(ord.customer)}${ord.email ? ` · ${escapeHtml(ord.email)}` : ''}${ord.total ? ` · ${escapeHtml(ord.total)}` : ''}
          </div>
          <ul class="bl-order__items">
            ${ord.items.map(it => `
              <li>
                ${escapeHtml(it.title)}${it.variant ? ` <em>(${escapeHtml(it.variant)})</em>` : ''}
                ${it.allergyFlag ? ` <span class="bl-tag">${escapeHtml(it.allergyFlag)}</span>` : ''}
                ${it.qty > 1 ? ` × <strong>${it.qty}</strong>` : ''}
                ${it.boxContents && it.boxContents.length ? `
                  <div class="bl-order__box-contents">
                    ${it.boxContents.map(b => `${b.qty}× ${escapeHtml(b.title)}`).join(' · ')}
                  </div>
                ` : ''}
                ${it.cupcakePick ? `<div style="font-size:13px;color:#6b5a55;margin-top:4px;">Pick: ${escapeHtml(it.cupcakePick)}</div>` : ''}
              </li>
            `).join('')}
          </ul>
          ${deliveryNote ? `<div class="bl-order__note">📝 Delivery instructions: ${escapeHtml(deliveryNote)}</div>` : ''}
        </div>
      `;
    });

    html += `</div></div>`;
    return html;
  }

  /* ── Render: per-order Order Cards ──────────────────────────────── */
  /*  Chris asked 2026-05-13: \"need a way to print out the order details for
   *  a specific order so we can hang it up on the specific day.\" The
   *  Fulfillment section above is compact (good reference, small type);
   *  this section renders ONE big card per order with everything the team
   *  needs to package — designed to be torn out one-per-page and taped
   *  to each tray.
   *
   *  Print behavior: each card is `break-after: page` so when the user
   *  hits \"Print everything\" or \"Order cards only\" they get one card
   *  per sheet. On screen, the cards stack with a generous margin so the
   *  same view is readable as a digital reference too.
   *
   *  Dateless orders (no Pickup/Delivery Date attribute) are included with
   *  a red border so they don't silently disappear from the print packet.
   *  Renee + Chris would rather see an oddity in print than discover it
   *  was missed only when the customer calls.
   */
  function renderOrderCards (filteredOrders, datelessOrders) {
    const all = [];
    Object.values(filteredOrders).forEach((o) => all.push({ ord: o, dateless: false }));
    Object.values(datelessOrders || {}).forEach((o) => all.push({ ord: o, dateless: true }));
    if (!all.length) return '';

    // Sort by pickup time (same as Fulfillment section). Dateless orders
    // float to the end so the chronological flow of the packet matches the
    // order in which staff will physically work the trays.
    all.sort((a, b) => {
      if (a.dateless !== b.dateless) return a.dateless ? 1 : -1;
      const ta = earliestTime(a.ord); const tb = earliestTime(b.ord);
      if (!ta && !tb) return a.ord.name.localeCompare(b.ord.name);
      if (!ta) return 1;
      if (!tb) return -1;
      return ta.localeCompare(tb);
    });

    let html = `
      <div class="bl-cards-wrap" data-cards>
        <div class="bl-cards-intro">
          <h2 style="margin:0 0 6px;font-size:18px;color:#2a1f1c;">Order Cards <span class="bl-count">(${all.length} cards)</span></h2>
          <p style="margin:0;font-size:13px;color:#6b5a55;line-height:1.5;">
            One card per order with everything you need to package it. Use <strong>“Order cards only”</strong> above to print one card per page — perfect for taping to each tray.
          </p>
        </div>
    `;

    all.forEach(({ ord, dateless }, i) => {
      const isDelivery = ord.items.some(it => /delivery/i.test(it.orderType));
      const pickupDate = ord.items.find(it => it.pickupDate)?.pickupDate || (dateless ? 'NO DATE SET' : '');
      const pickupTime = ord.items.find(it => it.pickupTime)?.pickupTime || (dateless ? '' : 'Time TBD');
      const deliveryZip = ord.items.find(it => it.deliveryZip)?.deliveryZip || '';
      const deliveryNote = ord.items.find(it => it.deliveryInstructions)?.deliveryInstructions || '';
      const allergyItems = ord.items.filter(it => it.allergyFlag);
      // Pull the customer phone if present on any item (we don't currently
      // collect it via the column map, but allow forward-compat). Falls back
      // to email when absent — at least staff have one contact channel on
      // the card.
      const contactLine = [ord.email].filter(Boolean).join(' · ');

      html += `
        <article class="bl-card${dateless ? ' bl-card--dateless' : ''}${isDelivery ? ' bl-card--delivery' : ' bl-card--pickup'}">
          <header class="bl-card__head">
            <div class="bl-card__order-num">${escapeHtml(ord.name)}</div>
            <div class="bl-card__type-badge">${isDelivery ? 'DELIVERY' : 'PICKUP'}${dateless ? ' · NO DATE' : ''}</div>
          </header>

          <div class="bl-card__when">
            <div class="bl-card__when-row">
              <span class="bl-card__when-label">Date</span>
              <span class="bl-card__when-val">${escapeHtml(pickupDate || '—')}</span>
            </div>
            <div class="bl-card__when-row">
              <span class="bl-card__when-label">Time</span>
              <span class="bl-card__when-val">${escapeHtml(pickupTime || '—')}</span>
            </div>
          </div>

          <div class="bl-card__customer">
            <div class="bl-card__customer-name">${escapeHtml(ord.customer)}</div>
            ${contactLine ? `<div class="bl-card__customer-contact">${escapeHtml(contactLine)}</div>` : ''}
            ${ord.total ? `<div class="bl-card__customer-total">${escapeHtml(ord.total)}</div>` : ''}
          </div>

          ${allergyItems.length ? `
            <div class="bl-card__alert">
              <strong>⚠️ ALLERGY — read before packing</strong>
              <ul>
                ${allergyItems.map(it => `
                  <li>${escapeHtml(it.title)}${it.qty > 1 ? ` × ${it.qty}` : ''} — <strong>${escapeHtml(it.allergyFlag.toUpperCase())}</strong></li>
                `).join('')}
              </ul>
            </div>
          ` : ''}

          <div class="bl-card__items">
            <div class="bl-card__items-head">Items to package</div>
            ${ord.items.map(it => `
              <div class="bl-card__item">
                <span class="bl-card__item-check" aria-hidden="true">☐</span>
                <div class="bl-card__item-body">
                  <div class="bl-card__item-title">
                    ${escapeHtml(it.title)}${it.variant ? ` <span class="bl-card__item-variant">(${escapeHtml(it.variant)})</span>` : ''}
                    ${it.allergyFlag ? `<span class="bl-tag">${escapeHtml(it.allergyFlag)}</span>` : ''}
                  </div>
                  ${it.boxContents && it.boxContents.length ? `
                    <ul class="bl-card__box-contents">
                      ${it.boxContents.map(b => `<li>${b.qty}× ${escapeHtml(b.title)}</li>`).join('')}
                    </ul>
                  ` : ''}
                  ${it.cupcakePick ? `<div class="bl-card__pick">Cupcake pick: <strong>${escapeHtml(it.cupcakePick)}</strong></div>` : ''}
                </div>
                <span class="bl-card__item-qty">× ${it.qty}</span>
              </div>
            `).join('')}
          </div>

          ${isDelivery ? `
            <div class="bl-card__delivery">
              <div class="bl-card__delivery-head">Delivery</div>
              ${deliveryZip ? `<div class="bl-card__delivery-zip">ZIP <strong>${escapeHtml(deliveryZip)}</strong></div>` : ''}
              ${deliveryNote ? `<div class="bl-card__delivery-note">📝 ${escapeHtml(deliveryNote)}</div>` : ''}
            </div>
          ` : ''}

          <footer class="bl-card__footer">
            <span class="bl-card__footer-label">Packed by</span>
            <span class="bl-card__footer-line"></span>
            <span class="bl-card__footer-label">Date</span>
            <span class="bl-card__footer-line bl-card__footer-line--short"></span>
          </footer>
        </article>
      `;
    });

    html += `</div>`;
    return html;
  }

  function renderFutureHeadsUp (currentDateFilter) {
    if (currentDateFilter === 'all') return '';
    // Find items for OTHER dates in the same set of orders
    const futureItems = [];
    Object.values(orders).forEach((ord) => {
      ord.items.forEach((it) => {
        if (it.pickupDate && it.pickupDate !== currentDateFilter) {
          const d = new Date(it.pickupDate);
          const c = new Date(currentDateFilter);
          if (!isNaN(d) && !isNaN(c) && d > c) {
            futureItems.push({ order: ord.name, customer: ord.customer, ...it });
          }
        }
      });
    });
    if (!futureItems.length) return '';

    // Group by date
    const byDate = {};
    futureItems.forEach(it => {
      (byDate[it.pickupDate] = byDate[it.pickupDate] || []).push(it);
    });

    let html = `
      <div class="bl-future">
        <h2>Heads up — coming later this week</h2>
    `;
    Object.keys(byDate).sort((a, b) => new Date(a) - new Date(b)).forEach(date => {
      html += `<div style="font-weight:700;margin:12px 0 6px;">${escapeHtml(date)}</div>`;
      byDate[date].forEach(it => {
        html += `
          <div class="bl-line">
            <span class="bl-line__name">
              ${escapeHtml(it.order)} · ${escapeHtml(it.title)}${it.qty > 1 ? ` × ${it.qty}` : ''}
              ${it.boxContents && it.boxContents.length ? `
                <div class="bl-order__box-contents" style="margin-top:4px;">
                  ${it.boxContents.map(b => `${b.qty}× ${escapeHtml(b.title)}`).join(' · ')}
                </div>
              ` : ''}
            </span>
          </div>
        `;
      });
    });
    html += `</div>`;
    return html;
  }

  /* ── Helpers ────────────────────────────────────────────────────── */
  function earliestTime (ord) {
    for (const it of ord.items) {
      if (it.pickupTime) return it.pickupTime;
    }
    return '';
  }

  function escapeHtml (s) {
    if (s === null || s === undefined) return '';
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  function showError (msg) {
    errorBox.innerHTML = `<strong>Heads up:</strong> ${msg}`;
    errorBox.hidden = false;
    reportBox.hidden = true;
    dateFilter.hidden = true;
  }
  function hideError () {
    errorBox.hidden = true;
    errorBox.innerHTML = '';
  }

  /* ── Boot ───────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
